// Smooth-union panel shader from the Cortera WebGL study. Coordinates are
// measured CSS pixels in the real hero, rather than fixed artboard positions.
// Plain GLSL ES 1.0 for a raw WebGL context: no three.js, so the attributes
// it used to inject (position, uv) are declared here.
export const VERTEX_SHADER = /* glsl */ `
	attribute vec2 aPosition;
	varying vec2 vUv;

	void main() {
		vUv = aPosition * 0.5 + 0.5;
		gl_Position = vec4(aPosition, 0.0, 1.0);
	}
`;

export const FRAGMENT_SHADER = /* glsl */ `
	precision highp float;

	varying vec2 vUv;

	uniform vec2 uArtboard;
	uniform float uScale;
	uniform float uK;
	uniform float uRadius;
	uniform vec3 uColour;
	uniform vec2 uCentre[3];
	uniform vec2 uHalf[3];
	uniform float uOn[3];
	uniform sampler2D uRestMask;
	uniform float uRestMix;

	// Signed distance to an axis-aligned rounded box centred on the origin.
	float sdRoundBox(vec2 p, vec2 halfSize, float r) {
		vec2 q = abs(p) - halfSize + r;
		return min(max(q.x, q.y), 0.0) + length(max(q, 0.0)) - r;
	}

	// Polynomial (quadratic) smooth minimum — the meniscus.
	float smin(float a, float b, float k) {
		float h = clamp(0.5 + 0.5 * (b - a) / k, 0.0, 1.0);
		return mix(b, a, h) - k * h * (1.0 - h);
	}

	void main() {
		// vUv is bottom-left origin; the artboard is top-left origin.
		vec2 p = vec2(vUv.x, 1.0 - vUv.y) * uArtboard;

		float d = 1.0e5;
		for (int i = 0; i < 3; i++) {
			if (uOn[i] < 0.5) continue;
			// A radius larger than the smaller half-extent inverts the box, so
			// clamp it as the panel grows out of its seed.
			float r = min(uRadius, min(uHalf[i].x, uHalf[i].y));
			d = smin(d, sdRoundBox(p - uCentre[i], uHalf[i], r), uK);
		}

		// One canvas pixel expressed in artboard units — antialiasing without
		// needing derivatives (and so without needing GLSL ES 3.0).
		float aa = 1.0 / max(uScale, 0.0001);
		float alpha = 1.0 - smoothstep(-aa, aa, d);
		// Converge to the exact DOM silhouette, without fading its solid interior.
		alpha = mix(alpha, texture2D(uRestMask, vUv).a, uRestMix);

		if (alpha <= 0.0) discard;

		// uColour is the panel's CSS colour in sRGB, written straight out: no
		// linear round trip, so it matches the DOM ground exactly. Premultiplied
		// for the default premultipliedAlpha canvas.
		gl_FragColor = vec4(uColour * alpha, alpha);
	}
`;
